import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import SaveConflict from '../../../common/components/save-conflict/save-conflict';
import { useTier, useUpdateTier } from '../contexts/tiers';
import {
    BREAKDOWN_STATE_LABEL, TIER_PHASE_LABEL,
    hasChanges, toUpdatePayload, validate, valuesFrom,
} from '../utils/form';
import TierFields from './fields';

import '../../clients/styles/form.css';

export default function EditTierForm({ tierId }) {
    const { data, isLoading, error, refetch } = useTier(tierId);

    if (isLoading) return <p className="client-form__loading">Cargando tier...</p>;

    if (error) {
        return (
            <p className="client-form__failure">
                {error.message || 'No se pudo cargar el tier'}
            </p>
        );
    }

    const tier = data?.tier ?? data;
    return <TierEditor key={tier.id} tier={tier} refetch={refetch} />;
}

// Which of the four content fields differ, for the conflict comparison. Only
// content is compared: sort_order and the two status axes need no revision, so
// they are never what a 409 is about.
const CONTENT_LABELS = {
    name: 'Nombre',
    description: 'Descripción',
    expected_outcome: 'Resultado esperado',
    open_questions: 'Dudas abiertas',
};

function TierEditor({ tier, refetch }) {
    const updateTier = useUpdateTier(tier.id);

    // THE ROW THE DRAFT CAME FROM, held apart from the live query.
    //
    // `base` is what was on screen when the person started typing, and it is
    // what the outgoing payload is diffed against so only real edits travel.
    // `tier` is whatever the server says right now. Keeping the two separate is
    // what lets a conflict show "mine" against "theirs" without either one
    // overwriting the draft.
    const [base, setBase] = useState(tier);
    const [values, setValues] = useState(() => valuesFrom(tier));
    const [errors, setErrors] = useState({});
    const [conflict, setConflict] = useState(false);

    const frozen = !!tier.frozen;
    const breakdownState = BREAKDOWN_STATE_LABEL[tier.breakdown_state] ?? tier.breakdown_state;

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const takeTheirs = async () => {
        const { data } = await refetch();
        const fresh = data?.tier ?? data;
        if (fresh) {
            setBase(fresh);
            setValues(valuesFrom(fresh));
        }
        setConflict(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate(values, { withProject: false });
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        // The revision that came with the row this draft is based on — NOT a
        // fresh one. Re-reading it here to dodge the 409 would complete exactly
        // the overwrite the 409 exists to stop.
        const payload = toUpdatePayload(values, base, base.breakdown_revision);

        if (!hasChanges(payload)) {
            toast('No hay cambios que guardar');
            return;
        }

        try {
            const saved = await updateTier.mutateAsync(payload);
            const row = saved?.tier ?? saved;
            // The row moves forward with its NEW revision, so a second save in
            // the same sitting does not conflict with the first.
            if (row) {
                setBase(row);
                setValues(valuesFrom(row));
            }
            setConflict(false);
            toast.success(
                row?.breakdown_state === 'draft' && base.breakdown_state === 'agreed'
                    ? 'Tier guardado. Volvió a borrador: hace falta acordarlo de nuevo.'
                    : 'Tier guardado',
            );
        } catch (err) {
            if (err.isRevisionConflict) {
                // The draft stays exactly where it is. Nothing is refetched into
                // the form until a person asks for it.
                setConflict(true);
                await refetch();
                return;
            }
            if (err.isFrozen) {
                await refetch();
                toast.error('El tier está congelado: tiene tareas autorizadas y su contenido no cambia.');
                return;
            }
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo guardar el tier');
        }
    };

    const phase = TIER_PHASE_LABEL[tier.phase] ?? tier.phase;
    const theirs = valuesFrom(tier);
    const diff = Object.entries(CONTENT_LABELS).map(([name, label]) => ({
        name, label, mine: values[name], theirs: theirs[name],
    }));

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">{tier.name}</h2>
                {/* Three axes, three readings. Collapsing them into one label
                    called "Estado" is how this screen would start lying. */}
                <p className="client-form__subtitle">
                    Tier #{tier.id}
                    {breakdownState && <> · Desglose: {breakdownState}</>}
                    {phase && <> · Fase: {phase}</>}
                    {base.breakdown_revision != null && <> · Revisión {base.breakdown_revision}</>}
                </p>
            </header>

            {conflict && (
                <SaveConflict
                    fields={diff}
                    onReload={takeTheirs}
                    onDismiss={() => setConflict(false)}
                />
            )}

            {frozen && (
                <p className="client-form__failure">
                    Este tier tiene tareas autorizadas, así que su contenido quedó congelado y no
                    se edita por ninguna puerta. Lo que sigue abierto es el orden, el estado del
                    tier y el del registro.
                </p>
            )}

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos del tier</h3>
                        <p className="client-form__section-text">
                            La fase la mueve el sistema, no este formulario. Cambiar el nombre,
                            la descripción, el resultado esperado o las dudas sube la revisión, y
                            devuelve a borrador un tier ya acordado.
                        </p>
                    </div>

                    <TierFields
                        projectLabel={tier.project?.name}
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={updateTier.isPending}
                        projectLocked
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/tiers" className="client-form__cancel">Volver</Link>
                    <button type="submit" className="client-form__submit" disabled={updateTier.isPending}>
                        {updateTier.isPending ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </form>

            <p className="client-form__aside">
                Las dependencias entre tiers —de cuáles espera este— tienen su propio
                endpoint y todavía no tienen pantalla.
            </p>
        </section>
    );
}
